import { FormEvent, useState } from "react";
import Button from "@mui/material/Button";
import CssBaseline from "@mui/material/CssBaseline";
import Link from "@mui/material/Link";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import classes from "./SignInSide.module.css";
import proteaO from "../UI/protea-o-suave-preview.png";
import PartnerLogos from "../UI/PartnerLogos";
import MarineJellyfish from "./MarineJellyfish";
import LandingFish from "../LandingFish";

const baseUrl = process.env.REACT_APP_API_HOST;

function Copyright() {
  return (
    <Typography className={classes.copyright} component="p">
      © {new Date().getFullYear()} Protea ·{" "}
      <Link color="inherit" href="https://pyxis.tech">
        Pyxis
      </Link>
    </Typography>
  );
}

function ProteaMark({ compact = false }: { compact?: boolean }) {
  return (
    <div className={`${classes.wordmark} ${compact ? classes.compact : ""}`} aria-label="Protea">
      <span className={classes.wordmarkText}>
        <span className={classes.wordmarkName} aria-hidden="true">
          <span>PR</span>
          <span className={classes.wordmarkMark}>
            <img src={proteaO} alt="" />
          </span>
          <span>TEA</span>
        </span>
        <span className={classes.wordmarkTagline}>observatorio ambiental</span>
      </span>
    </div>
  );
}

const theme = createTheme({
  palette: {
    primary: { main: "#0b7d87" },
    secondary: { main: "#18a793" },
  },
  typography: {
    fontFamily: "Inter, Avenir Next, Segoe UI, sans-serif",
  },
});

export default function SignUpSide() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);

    setIsLoading(true);
    fetch(`${baseUrl}/auth/register`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email: data.get("email"),
        password: data.get("password"),
        first_name: data.get("first_name"),
        birthdate: data.get("birthdate"),
      }),
    })
      .then(async (res) => {
        if (!res.ok) {
          let errorMessage = "No se pudo crear la cuenta.";
          try {
            const contentType = res.headers.get("content-type");
            if (contentType?.includes("application/json")) {
              const response = await res.json();
              if (response?.detail) errorMessage = response.detail;
            } else {
              const text = await res.text();
              if (text) errorMessage = text;
            }
          } catch (parseError) {
            console.error("Error parsing response:", parseError);
            errorMessage = `No se pudo crear la cuenta (${res.status}).`;
          }
          throw new Error(errorMessage);
        }
        navigate("/");
      })
      .catch((error) => {
        setIsLoading(false);
        alert(error.message);
      });
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <main className={classes.page}>
        <section className={classes.storyPanel} aria-labelledby="signup-title">
          <div className={classes.storyContent}>
            <ProteaMark />
            <span className={classes.eyebrow}>Tecnología para cuidar la costa</span>
            <h1 id="signup-title" className={classes.storyTitle}>
              Mirar el mar también es cuidarlo.
            </h1>
            <p className={classes.storyText}>
              Protea convierte señales ambientales en decisiones claras para entender,
              anticipar y proteger nuestros ecosistemas.
            </p>
            <div className={classes.storyMeta} aria-label="Capacidades de Protea">
              <span className={classes.metaPill}>Datos en tiempo real</span>
              <span className={classes.metaPill}>Alertas inteligentes</span>
              <span className={classes.metaPill}>Memoria ambiental</span>
            </div>
            <PartnerLogos className={classes.partnerLogos} label="Powered by" />
          </div>

          <div className={classes.oceanScene} aria-hidden="true">
            <svg className={`${classes.wave} ${classes.waveBack}`} viewBox="0 0 1000 260" preserveAspectRatio="none">
              <path fill="currentColor" d="M0 128c115-60 205 42 320-4 128-51 192-47 310 5 128 56 210-56 370-4v135H0Z" />
            </svg>
            <svg className={`${classes.wave} ${classes.waveMid}`} viewBox="0 0 1000 260" preserveAspectRatio="none">
              <path fill="currentColor" d="M0 156c105-45 190 17 290-18 128-44 225 76 350 23 138-58 220-25 360 16v83H0Z" />
            </svg>
            <svg className={`${classes.wave} ${classes.waveFront}`} viewBox="0 0 1000 260" preserveAspectRatio="none">
              <path fill="currentColor" d="M0 190c120-48 205 35 325-4 104-34 190 41 298 8 150-46 229 2 377-22v88H0Z" />
            </svg>
            <span className={`${classes.bubble} ${classes.bubbleOne}`} />
            <span className={`${classes.bubble} ${classes.bubbleTwo}`} />
            <span className={`${classes.bubble} ${classes.bubbleThree}`} />
            <span className={`${classes.bubble} ${classes.bubbleFour}`} />
            <MarineJellyfish />
            <div className={classes.marineLife} aria-hidden="true">
              <LandingFish />
            </div>
            <div className={classes.starFish}>
              <svg viewBox="0 0 80 80" role="presentation">
                <path d="M40 7c7 0 7 14 11 19 6-2 18-9 22-3 4 6-8 14-12 19 5 5 16 14 11 20-5 6-17-3-23-5-3 6-4 20-11 20s-8-14-11-20c-6 2-18 11-23 5-5-6 6-15 11-20-4-5-16-13-12-19 4-6 16 1 22 3 4-5 4-19 11-19Z" />
                <path d="M40 34c4 0 7 3 7 7s-3 7-7 7-7-3-7-7 3-7 7-7Z" />
                <circle cx="40" cy="37" r="1.2" fill="currentColor" stroke="none" />
              </svg>
            </div>
          </div>
          <div className={classes.careNote}>monitoreo · cuidado · futuro</div>
        </section>

        <section className={classes.authPanel} aria-label="Registro en Protea">
          <div className={classes.authContent}>
            <div className={classes.mobileBrand}><ProteaMark compact /></div>
            <div className={classes.authKicker}><span className={classes.statusDot} /> Portal Protea</div>
            <h2 className={classes.authTitle}>Creá tu cuenta.</h2>
            <p className={classes.authIntro}>
              Sumate al observatorio para seguir lo que está pasando en el ambiente.
            </p>
            <Box component="form" noValidate onSubmit={handleSubmit} className={classes.form}>
              <TextField
                required
                fullWidth
                id="email"
                label="Correo electrónico"
                name="email"
                autoComplete="email"
                autoFocus
              />
              <TextField
                required
                fullWidth
                name="password"
                label="Contraseña"
                type="password"
                id="password"
                autoComplete="new-password"
              />
              <TextField
                required
                fullWidth
                name="first_name"
                label="Nombre"
                id="first_name"
                autoComplete="given-name"
              />
              <TextField
                required
                fullWidth
                name="birthdate"
                label="Fecha de nacimiento"
                type="date"
                InputLabelProps={{ shrink: true }}
                id="birthdate"
                autoComplete="bday"
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                className={classes.submitButton}
                disabled={isLoading}
              >
                {isLoading ? "Creando cuenta…" : "Crear cuenta"}
              </Button>
              <div className={classes.formLinks}>
                <Link component={RouterLink} to="/">Ya tengo una cuenta</Link>
              </div>
              <Copyright />
            </Box>
          </div>
        </section>
      </main>
    </ThemeProvider>
  );
}
